import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    Search, User, LogOut, Plus, Settings, ChevronDown, 
    Heart, Bell, Moon, Sun, Smartphone, Ticket, Store, TrendingUp,
    Bookmark, Layout, Settings2, Crown,
    ArrowLeft
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import MarcaTexto from './brand/MarcaTexto';
import AnimatedBrandIcon from './AnimatedBrandIcon';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { API } from '../config/apiBase';
import { useFeature } from '../context/FeaturesContext';
import useOcultarAoRolar from '../hooks/useOcultarAoRolar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';


// Theme Layout Selector Component
const ThemeLayoutSelector = ({ layoutMode, onToggle, compact = false }) => {
    if (compact) {
        return (
            <div className="theme-selector-toggle">
                <button 
                    onClick={() => onToggle()}
                    className={`theme-selector-btn ${layoutMode === 'classic' ? 'active' : ''}`}
                >
                    Clássico
                </button>
                <button 
                    onClick={() => onToggle()}
                    className={`theme-selector-btn ${layoutMode === 'minimal' ? 'active' : ''}`}
                >
                    Minimalista
                </button>
            </div>
        );
    }
    
    return (
        <div className="flex items-center gap-2">
            <button 
                onClick={() => onToggle()}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    layoutMode === 'classic' 
                        ? 'bg-[var(--gold-2)] text-[#0A0A0A]' 
                        : 'bg-[var(--surface-2)] text-[var(--muted)] border border-[var(--border)] hover:text-[var(--text)]'
                }`}
            >
                Clássico
            </button>
            <button 
                onClick={() => onToggle()}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    layoutMode === 'minimal' 
                        ? 'bg-[var(--gold-2)] text-[#0A0A0A]' 
                        : 'bg-[var(--surface-2)] text-[var(--muted)] border border-[var(--border)] hover:text-[var(--text)]'
                }`}
            >
                Minimalista
            </button>
        </div>
    );
};

// Sprint 1 #2 — Dropdown com lojas verificadas (Promobit-style).
const KNOWN_STORES = [
    { slug: 'mercado-livre', label: 'Mercado Livre', emoji: '🟡' },
    { slug: 'amazon', label: 'Amazon', emoji: '🟠' },
    { slug: 'shopee', label: 'Shopee', emoji: '🟢' },
    { slug: 'aliexpress', label: 'AliExpress', emoji: '🔴' },
    { slug: 'magalu', label: 'Magalu', emoji: '🔵' },
    { slug: 'americanas', label: 'Americanas', emoji: '🔴' },
    { slug: 'samsung', label: 'Samsung', emoji: '⚫' },
];

function NavStoresDropdown() {
    const [open, setOpen] = React.useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(o => !o)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                className="text-sm flex items-center gap-1.5 transition-colors hover:text-[var(--gold-2)]"
                style={{ color: 'var(--muted)', fontWeight: 480 }}
                data-testid="nav-lojas"
            >
                <Store className="w-4 h-4" strokeWidth={1.75} />
                Lojas
                <Settings2 className="w-2.5 h-2.5 opacity-50" />
            </button>
            {open && (
                <div className="absolute left-0 top-full mt-2 z-50 rounded-xl py-2 min-w-[200px] shadow-xl"
                    style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    {KNOWN_STORES.map(s => (
                        <Link
                            key={s.slug}
                            to={`/loja/${s.slug}`}
                            onClick={() => setOpen(false)}
                            className="block px-4 py-2 text-sm hover:bg-[var(--surface-2)] transition-colors flex items-center gap-2"
                            style={{ color: 'var(--text)' }}
                        >
                            <span>{s.emoji}</span>
                            {s.label}
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

// Sprint 1 #3 — slots de banner promo no menu (controlados por system_config).
function NavPromoSlots() {
    const [slots, setSlots] = React.useState([]);
    React.useEffect(() => {
        let mounted = true;
        fetch(`${API}/nav-promo-slots`).then(r => r.ok ? r.json() : { slots: [] })
            .then(d => mounted && setSlots(Array.isArray(d?.slots) ? d.slots : []))
            .catch(() => {});
        return () => { mounted = false; };
    }, []);
    if (!slots.length) return null;
    return (
        <>
            {slots.map((s, i) => (
                <a key={i} href={s.url || '#'} target={s.url ? '_blank' : '_self'} rel="noopener noreferrer"
                    className="text-xs px-2.5 py-1 rounded-md font-medium transition-opacity hover:opacity-90 whitespace-nowrap"
                    style={{ background: s.color || 'var(--sv-gold-btn)', color: s.text_color || '#0A0A0A' }}>
                    {s.label}
                </a>
            ))}
        </>
    );
}

// Faixa promocional do topo — gerenciada na aba Banners (position: "topbar").
// Clicavel, cor/texto/imagem/link editaveis; some se nenhuma faixa estiver ativa.
// A última faixa vista fica guardada no navegador e é desenhada na hora; a
// resposta da API confirma ou troca. Sem isso, a faixa chegava depois do resto e
// empurrava a página inteira para baixo (medido: 29 px no celular).
const CHAVE_TOPBAR = 'sv-topbar-cache';
const lerTopbarGuardada = () => {
    try { return JSON.parse(localStorage.getItem(CHAVE_TOPBAR)) || null; } catch (e) { return null; }
};
function TopbarPromo({ isClassic }) {
    const [bar, setBar] = React.useState(lerTopbarGuardada);
    React.useEffect(() => {
        let mounted = true;
        fetch(`${API}/banners?position=topbar`)
            .then(r => r.ok ? r.json() : [])
            .then(d => {
                const atual = Array.isArray(d) && d.length ? d[0] : null;
                try { if (atual) localStorage.setItem(CHAVE_TOPBAR, JSON.stringify(atual)); else localStorage.removeItem(CHAVE_TOPBAR); } catch (e) { /* sem storage */ }
                if (mounted) setBar(atual);
            })
            .catch(() => {});
        return () => { mounted = false; };
    }, []);

    if (!bar) return null; // desativada / nenhuma faixa ativa

    const hasCustomBg = !!(bar.bg_color && bar.bg_color.trim());
    const background = hasCustomBg
        ? bar.bg_color
        : (isClassic
            ? 'linear-gradient(90deg, var(--gold-3) 0%, var(--gold-2) 50%, var(--gold-3) 100%)'
            : 'var(--surface-2)');
    const color = (bar.text_color && bar.text_color.trim())
        ? bar.text_color
        : (isClassic ? '#0A0A0A' : '#8A8A8A');
    const link = bar.link || '#';
    const isExternal = /^https?:\/\//i.test(link);
    const imgSrc = bar.image_url
        ? (bar.image_url.startsWith('http') ? bar.image_url : `${API.replace(/\/api\/?$/, '')}${bar.image_url}`)
        : '';

    const inner = (
        <div className="site-container mx-auto px-3 sm:px-4 flex items-center justify-center gap-2 text-[11px] sm:text-xs whitespace-nowrap overflow-x-auto" style={{ color, scrollbarWidth: 'none' }}>
            {imgSrc ? (
                <img src={imgSrc} alt={bar.title || 'Promoção'} className="object-contain" style={{ maxHeight: 28 }} />
            ) : (
                <span className="font-semibold transition-opacity hover:opacity-80">{bar.title}</span>
            )}
        </div>
    );

    const wrapStyle = {
        background,
        borderBottom: (isClassic || hasCustomBg) ? 'none' : '1px solid var(--border)',
    };

    return (
        <div className="block promo-bar" style={wrapStyle} data-testid="topbar-promo">
            {/* O respiro vertical fica dentro do link: a faixa inteira é o alvo de toque. */}
            {isExternal
                ? <a href={link} target="_blank" rel="noopener noreferrer" className="block py-1.5">{inner}</a>
                : <Link to={link} className="block py-1.5">{inner}</Link>}
        </div>
    );
}

export const Navbar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const { user, logout, isAdmin, isApproved, canAccessSystem } = useAuth();
    const { toggleTheme, isDark, layoutMode, toggleLayoutMode, isClassic } = useTheme();
    const showPlans = useFeature('show_plans');
    const navigate = useNavigate();
    const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
    const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    // Celular: o header some ao descer e volta ao subir. Nunca com a busca ou o menu abertos.
    const headerOculto = useOcultarAoRolar({ pausado: mobileSearchOpen || mobileDropdownOpen });
    const mobileSearchRef = useRef(null);

    // Listen for tour force-open event: abre o dropdown CERTO segundo o viewport.
    // Antes abria o dropdown mobile mesmo em desktop (trigger oculto md:hidden),
    // o que fazia o Radix posicionar o painel no canto esquerdo (trigger 0px).
    useEffect(() => {
        const handler = (e) => {
            const open = !!e.detail?.open;
            const isMobileView = typeof window !== 'undefined' && window.innerWidth < 768;
            if (isMobileView) {
                setMobileDropdownOpen(open);
                setDesktopDropdownOpen(false);
            } else {
                setDesktopDropdownOpen(open);
                setMobileDropdownOpen(false);
            }
        };
        window.addEventListener('sv_force_avatar_dropdown', handler);
        return () => window.removeEventListener('sv_force_avatar_dropdown', handler);
    }, []);

    // Fetch notifications
    useEffect(() => {
        if (user) {
            fetchUnreadCount();
        }
    }, [user]);

    const fetchUnreadCount = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API}/notifications/unread-count`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setUnreadCount(data.count);
            }
        } catch (err) {}
    };

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API}/notifications`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                setNotifications(await res.json());
            }
        } catch (err) {}
    };

    const markAllRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${API}/notifications/read-all`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (err) {}
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/busca?q=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
        }
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    // Icon button component for consistency
    const IconButton = ({ icon: Icon, label, to, onClick, active, badge }) => {
        const content = (
            <div className="flex flex-col items-center gap-1 cursor-pointer group">
                <div 
                    className={`icon-btn w-10 h-10 rounded-lg flex items-center justify-center transition-all relative
                        ${active ? 'active' : ''}
                    `}
                >
                    <Icon 
                        className="w-5 h-5 transition-colors group-hover:text-[var(--gold-2)]" 
                        style={{ color: active ? 'var(--gold-2)' : 'var(--muted-2)' }}
                        strokeWidth={1.5}
                    />
                    {badge && (
                        <span 
                            className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center"
                            style={{ background: 'var(--gold-2)', color: 'var(--sv-on-accent)' }}
                        >
                            {badge}
                        </span>
                    )}
                </div>
                <span 
                    className="text-[10px] transition-colors group-hover:text-[var(--gold-2)]"
                    style={{ color: 'var(--muted-2)' }}
                >
                    {label}
                </span>
            </div>
        );

        if (to) {
            return <Link to={to}>{content}</Link>;
        }
        if (onClick) {
            return <button onClick={onClick}>{content}</button>;
        }
        return content;
    };

    return (
        <nav className={`navbar-glass sticky top-0 z-50 ${headerOculto ? 'sv-nav-oculto' : ''}`} data-testid="navbar">
            {/* Faixa promocional do topo — gerenciada na aba Banners (position: topbar) */}
            <TopbarPromo isClassic={isClassic} />

            {/* Main Header */}
            <div className="site-container mx-auto px-3 sm:px-6 lg:px-8 sv-header-row">
                {/* DESKTOP header */}
                <div className="hidden md:flex items-center justify-between h-[68px] gap-6">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
                        <AnimatedBrandIcon size={36} animate={false} glow={false} />
                        <MarcaTexto className="text-xl font-bold tracking-wide" style={{ letterSpacing: '0.08em' }} />
                    </Link>

                    {/* Search - Desktop */}
                    <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl" role="search">
                        <div className="relative w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                            <input
                                type="text"
                                placeholder="Buscar produto, marca ou loja"
                                aria-label="Buscar ofertas"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="search-input w-full pl-11 pr-4 py-2.5 text-sm"
                                data-testid="search-input"
                            />
                        </div>
                    </form>

                    {/* Desktop Nav - Aligned */}
                    <div className="hidden md:flex items-center gap-3">
                        {/* 1. Publicar - pill sempre com o texto "PUBLICAR" */}
                        <Link to={user ? "/criar-oferta" : "/login"} className="flex items-center">
                            <button className="publish-btn" data-testid="publish-btn">
                                <Plus className="w-5 h-5 flex-shrink-0" strokeWidth={2.5} />
                                <span className="publish-text">Publicar</span>
                            </button>
                        </Link>

                        {/* 2. Baixar app - sempre visivel, ao lado do Publicar */}
                        <IconButton icon={Smartphone} label="Baixar app" to="/downloads" />

                        {user ? (
                            <>
                                {/* 3. Wishlist - icon only */}
                                <IconButton icon={Heart} label="Desejos" to="/perfil?tab=wishlist" />
                                
                                {/* 4. Notifications - icon only */}
                                <DropdownMenu onOpenChange={(open) => open && fetchNotifications()}>
                                    <DropdownMenuTrigger asChild>
                                        <button className="flex flex-col items-center gap-1 cursor-pointer group">
                                            <div className="icon-btn w-10 h-10 rounded-lg flex items-center justify-center transition-all relative">
                                                <Bell className="w-5 h-5 transition-colors group-hover:text-[var(--gold-2)]" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                {unreadCount > 0 && (
                                                    <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: 'var(--gold-2)', color: 'var(--sv-on-accent)' }}>
                                                        {unreadCount > 9 ? '9+' : unreadCount}
                                                    </span>
                                                )}
                                            </div>
                                            <span className="text-[10px] transition-colors group-hover:text-[var(--gold-2)]" style={{ color: 'var(--muted-2)' }}>Alertas</span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-80" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                        <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                                            <span className="font-semibold" style={{ color: 'var(--text)' }}>Notificações</span>
                                            {unreadCount > 0 && (
                                                <button onClick={markAllRead} className="text-xs hover:text-[var(--gold-2)]" style={{ color: 'var(--muted)' }}>
                                                    Marcar todas como lidas
                                                </button>
                                            )}
                                        </div>
                                        <div className="max-h-80 overflow-y-auto">
                                            {notifications.length === 0 ? (
                                                <div className="px-4 py-8 text-center" style={{ color: 'var(--muted)' }}>
                                                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                                    <p className="text-sm">Nenhuma notificação</p>
                                                </div>
                                            ) : (
                                                notifications.slice(0, 10).map((notif) => (
                                                    <div 
                                                        key={notif.notification_id}
                                                        className={`px-4 py-3 cursor-pointer hover:bg-[var(--surface-2)] ${!notif.is_read ? 'bg-[var(--gold-bg)]' : ''}`}
                                                        style={{ borderBottom: '1px solid var(--border)' }}
                                                        onClick={() => notif.link && navigate(notif.link)}
                                                    >
                                                        <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{notif.title}</p>
                                                        <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>{notif.message}</p>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                        <div className="px-4 py-2" style={{ borderTop: '1px solid var(--border)' }}>
                                            <Link to="/perfil?tab=alerts" className="text-xs hover:text-[var(--gold-2)]" style={{ color: 'var(--gold-2)' }}>
                                                Ver todos os alertas →
                                            </Link>
                                        </div>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                
                                {/* 5. Theme Toggle - Icon only, same style as others */}
                                <div className="flex flex-col items-center gap-1 cursor-pointer group" onClick={toggleLayoutMode}>
                                    <div 
                                        className="icon-btn w-10 h-10 rounded-lg flex items-center justify-center"
                                        title={layoutMode === 'classic' ? 'Minimalista' : 'Clássico'}
                                    >
                                        <Settings2 
                                            className="w-5 h-5 transition-colors group-hover:text-[var(--gold-2)]" 
                                            style={{ color: 'var(--muted-2)' }} 
                                            strokeWidth={1.5} 
                                        />
                                    </div>
                                    <span className="text-[10px] transition-colors group-hover:text-[var(--gold-2)]" style={{ color: 'var(--muted-2)' }}>
                                        {layoutMode === 'classic' ? 'Clássico' : 'Minimal'}
                                    </span>
                                </div>
                                
                                {/* 6. User Menu */}
                                <DropdownMenu open={desktopDropdownOpen} onOpenChange={setDesktopDropdownOpen}>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            className="flex flex-col items-center gap-1 cursor-pointer group"
                                            data-testid="user-menu-trigger"
                                        >
                                            <div 
                                                className="w-10 h-10 rounded-full overflow-hidden"
                                                style={{ border: '1px solid var(--border)' }}
                                            >
                                                {user.picture ? (
                                                    <img src={resolveImageUrl(user.picture)} alt={user.name} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div 
                                                        className="w-full h-full flex items-center justify-center font-medium"
                                                        style={{ background: 'var(--surface-2)', color: 'var(--gold-2)' }}
                                                    >
                                                        {user.name?.charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                            </div>
                                            <span className="text-[10px] truncate max-w-[60px] group-hover:text-[var(--gold-2)] transition-colors" style={{ color: 'var(--muted-2)' }}>
                                                {user.name?.split(' ')[0]}
                                            </span>
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-64 sv-dropdown-menu" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
                                        {/* User Header */}
                                        <div className="px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                                            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{user.name}</p>
                                            <p className="text-xs" style={{ color: 'var(--gold-2)' }}>Nível {user.level || 1} - {user.level_name || 'Novato'}</p>
                                            <div className="mt-2">
                                                <div className="flex items-center justify-between text-[10px] mb-1" style={{ color: 'var(--muted-2)' }}>
                                                    <span>Próximo nível</span>
                                                    <span>{user.points || 0} / {user.next_level_threshold || 100}</span>
                                                </div>
                                                <div className="progress-bar">
                                                    <div 
                                                        className="progress-fill" 
                                                        style={{ width: `${Math.min(((user.points || 0) / (user.next_level_threshold || 100)) * 100, 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <DropdownMenuItem asChild>
                                            <Link to="/perfil" className="flex items-center gap-3 cursor-pointer py-2" data-testid="menu-perfil">
                                                <User className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                <span style={{ color: 'var(--text)' }}>Meu Perfil</span>
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/perfil?tab=wishlist" className="flex items-center gap-3 cursor-pointer py-2">
                                                <Heart className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                <span style={{ color: 'var(--text)' }}>Lista de desejos</span>
                                            </Link>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem asChild>
                                            <Link to="/perfil?tab=deals" className="flex items-center gap-3 cursor-pointer py-2">
                                                <Bookmark className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                <span style={{ color: 'var(--text)' }}>Minhas ofertas</span>
                                            </Link>
                                        </DropdownMenuItem>
                                        
                                        {canAccessSystem && (
                                            <DropdownMenuItem asChild className="sv-dropdown-item sv-dropdown-item-gold">
                                                <Link to="/sistema" className="flex items-center gap-3 cursor-pointer py-2" data-testid="menu-painel-gestao">
                                                    <Settings className="w-4 h-4" style={{ color: 'var(--gold-2)' }} strokeWidth={1.5} />
                                                    <span style={{ color: 'var(--gold-2)', fontWeight: 600 }}>Painel de Gestão</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        )}

                                        {isAdmin && (
                                            <DropdownMenuItem asChild className="sv-dropdown-item">
                                                <Link to="/admin" className="flex items-center gap-3 cursor-pointer py-2" data-testid="menu-admin-site">
                                                    <Settings className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                    <span style={{ color: 'var(--text)' }}>Painel do Site</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                        
                                        <DropdownMenuSeparator style={{ background: 'var(--border)' }} />
                                        
                                        {/* Theme Layout Toggle */}
                                        <div className="px-2 py-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-3">
                                                    <Layout className="w-4 h-4" style={{ color: '#8A8A8A' }} strokeWidth={1.5} />
                                                    <span className="text-sm" style={{ color: 'var(--text)' }}>Tema do layout</span>
                                                </div>
                                            </div>
                                            <div className="theme-selector-toggle ml-7">
                                                <button 
                                                    onClick={toggleLayoutMode}
                                                    className={`theme-selector-btn ${layoutMode === 'classic' ? 'active' : ''}`}
                                                    data-testid="layout-classic-btn"
                                                >
                                                    Clássico
                                                </button>
                                                <button 
                                                    onClick={toggleLayoutMode}
                                                    className={`theme-selector-btn ${layoutMode === 'minimal' ? 'active' : ''}`}
                                                    data-testid="layout-minimal-btn"
                                                >
                                                    Minimalista
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {/* Theme Toggle - Night Mode */}
                                        <div className="px-2 py-2 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                {isDark ? <Moon className="w-4 h-4" style={{ color: '#8A8A8A' }} strokeWidth={1.5} /> : <Sun className="w-4 h-4" style={{ color: '#8A8A8A' }} strokeWidth={1.5} />}
                                                <span className="text-sm" style={{ color: 'var(--text)' }}>Modo noturno</span>
                                            </div>
                                            <Switch 
                                                checked={isDark} 
                                                onCheckedChange={toggleTheme}
                                                data-testid="theme-toggle"
                                            />
                                        </div>
                                        
                                        <DropdownMenuSeparator style={{ background: 'var(--border)' }} />
                                        
                                        <DropdownMenuItem 
                                            onClick={handleLogout}
                                            className="flex items-center gap-3 cursor-pointer py-2"
                                            data-testid="menu-logout"
                                        >
                                            <LogOut className="w-4 h-4" style={{ color: '#EF4444' }} strokeWidth={1.5} />
                                            <span style={{ color: '#EF4444' }}>Sair</span>
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            <>
                                {/* Not logged in: Entrar with same alignment */}
                                <Link to="/login" className="flex flex-col items-center gap-1 cursor-pointer group">
                                    <div 
                                        className="w-10 h-10 rounded-lg flex items-center justify-center transition-all"
                                        style={{ background: 'var(--gold-2)' }}
                                        data-testid="nav-login"
                                    >
                                        <User className="w-5 h-5" style={{ color: '#0A0A0A' }} strokeWidth={1.5} />
                                    </div>
                                    <span className="text-[10px] transition-colors group-hover:text-[var(--gold-2)]" style={{ color: 'var(--muted-2)' }}>
                                        Entrar
                                    </span>
                                </Link>
                            </>
                        )}
                    </div>
                </div>

                {/* MOBILE header - Logo + Search Icon + Publish + Avatar */}
                <div className="flex md:hidden items-center h-14 gap-2">
                        {/* Mobile search expanded overlay */}
                        {mobileSearchOpen && (
                            <div className="mobile-search-overlay" data-testid="mobile-search-overlay">
                                <button onClick={() => { setMobileSearchOpen(false); setSearchQuery(''); }} className="mobile-search-back" data-testid="mobile-search-back">
                                    <ArrowLeft size={20} style={{ color: 'var(--text)' }} />
                                </button>
                                <form onSubmit={(e) => { handleSearch(e); setMobileSearchOpen(false); }} className="flex-1 min-w-0">
                                    <input
                                        ref={mobileSearchRef}
                                        type="text"
                                        placeholder="Buscar oferta..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        data-testid="mobile-search-input"
                                        autoFocus
                                    />
                                </form>
                            </div>
                        )}

                        {/* Logo compact */}
                        {/* 44 px de alvo em volta do ícone de 26 px; a margem negativa mantém o ícone no lugar. */}
                        <Link to="/" aria-label="Super Vitrine, página inicial" className="flex-shrink-0 flex items-center justify-center w-11 h-11 -ml-[9px]" data-testid="navbar-logo-mobile">
                            <AnimatedBrandIcon size={26} animate={false} glow={false} />
                        </Link>

                        {/* Spacer */}
                        <div className="flex-1" />

                        {/* Search icon trigger */}
                        <button
                            className="sv-toque-44 w-10 h-10 rounded-lg flex items-center justify-center"
                            style={{ background: 'var(--surface-2)' }}
                            onClick={() => { setMobileSearchOpen(true); setTimeout(() => mobileSearchRef.current?.focus(), 100); }}
                            aria-label="Buscar ofertas"
                            data-testid="mobile-search-trigger"
                        >
                            <Search className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                        </button>

                        {/* Publish button - pill com texto "PUBLICAR" tambem no mobile */}
                        <Link to={user ? "/criar-oferta" : "/login"} className="flex-shrink-0">
                            <button className="publish-btn sv-toque-44" data-testid="mobile-publish-btn">
                                <Plus className="w-4 h-4 flex-shrink-0" strokeWidth={2.5} />
                                <span className="publish-text">Publicar</span>
                            </button>
                        </Link>

                        {/* Baixar app - icone ao lado do Publicar */}
                        <Link to="/downloads" className="flex-shrink-0" aria-label="Baixar app">
                            <button className="sv-toque-44 w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--surface-2)' }} data-testid="mobile-app-btn">
                                <Smartphone className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                            </button>
                        </Link>

                        {user ? (
                            <DropdownMenu open={mobileDropdownOpen} onOpenChange={setMobileDropdownOpen}>
                                <DropdownMenuTrigger asChild>
                                    <button className="sv-toque-44 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--gold-2)' }} data-testid="mobile-avatar-btn">
                                        {user.picture ? (
                                            <img src={resolveImageUrl(user.picture)} alt="" className="w-10 h-10 rounded-full object-cover" />
                                        ) : (
                                            <span className="text-xs font-bold" style={{ color: '#0A0A0A' }}>{user.name?.charAt(0).toUpperCase()}</span>
                                        )}
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56 sv-dropdown-menu" style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12 }}>
                                    <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{user.name}</p>
                                        <p className="text-[10px]" style={{ color: 'var(--muted-2)' }}>{user.email}</p>
                                    </div>
                                    <DropdownMenuItem asChild>
                                        <Link to="/perfil" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm" data-testid="mobile-menu-perfil">
                                            <User className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                            <span style={{ color: 'var(--text)' }}>Meu Perfil</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/perfil?tab=wishlist" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm">
                                            <Heart className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                            <span style={{ color: 'var(--text)' }}>Lista de Desejos</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/categorias" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm">
                                            <Store className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                            <span style={{ color: 'var(--text)' }}>Categorias</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/cupons" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm">
                                            <Ticket className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                            <span style={{ color: 'var(--text)' }}>Cupons</span>
                                        </Link>
                                    </DropdownMenuItem>
                                    {canAccessSystem && (
                                        <DropdownMenuItem asChild className="sv-dropdown-item sv-dropdown-item-gold">
                                            <Link to="/sistema" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm" data-testid="mobile-menu-painel-gestao">
                                                <Settings className="w-4 h-4" style={{ color: 'var(--gold-2)' }} strokeWidth={1.5} />
                                                <span style={{ color: 'var(--gold-2)', fontWeight: 600 }}>Painel de Gestão</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    {isAdmin && (
                                        <DropdownMenuItem asChild className="sv-dropdown-item">
                                            <Link to="/admin" className="flex items-center gap-2.5 cursor-pointer py-2 text-sm" data-testid="mobile-menu-admin-site">
                                                <Settings className="w-4 h-4" style={{ color: 'var(--muted-2)' }} strokeWidth={1.5} />
                                                <span style={{ color: 'var(--text)' }}>Painel do Site</span>
                                            </Link>
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator style={{ background: 'var(--border)' }} />
                                    {/* Theme & Layout toggles */}
                                    <div className="px-3 py-2 space-y-2" style={{ borderBottom: '1px solid var(--border)' }}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px]" style={{ color: 'var(--muted-2)' }}>Tema</span>
                                            <button onClick={toggleTheme} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]" style={{ background: 'var(--surface-2)', color: 'var(--text)' }} data-testid="mobile-theme-toggle">
                                                {isDark ? <Moon className="w-3 h-3" /> : <Sun className="w-3 h-3" />}
                                                {isDark ? 'Escuro' : 'Claro'}
                                            </button>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[11px]" style={{ color: 'var(--muted-2)' }}>Layout</span>
                                            <button onClick={toggleLayoutMode} className="flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]" style={{ background: 'var(--surface-2)', color: 'var(--text)' }} data-testid="mobile-layout-toggle">
                                                <Layout className="w-3 h-3" />
                                                {isClassic ? 'Clássico' : 'Minimal'}
                                            </button>
                                        </div>
                                    </div>
                                    <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2.5 cursor-pointer py-2 text-sm" data-testid="mobile-menu-logout">
                                        <LogOut className="w-4 h-4" style={{ color: '#EF4444' }} strokeWidth={1.5} />
                                        <span style={{ color: '#EF4444' }}>Sair</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/login" aria-label="Entrar" className="sv-toque-44 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--gold-2)' }} data-testid="mobile-login-btn">
                                <User className="w-4 h-4" style={{ color: '#0A0A0A' }} strokeWidth={2} />
                            </Link>
                        )}
                    </div>
            </div>

            {/* Sub Navigation - Desktop only */}
            <div className="block sv-subnav">
                <div className="site-container mx-auto px-3 sm:px-6 lg:px-8">
                    <div className="flex items-center gap-4 sm:gap-6 py-2 sm:py-2.5 overflow-x-auto whitespace-nowrap" style={{ scrollbarWidth: 'none' }}>
                        <Link 
                            to="/categorias" 
                            className="text-sm flex items-center gap-1.5 transition-colors hover:text-[var(--gold-2)]" 
                            style={{ color: 'var(--muted)', fontWeight: 480 }}
                        >
                            <Store className="w-4 h-4" strokeWidth={1.75} />
                            Categorias
                        </Link>
                        <Link 
                            to="/cupons" 
                            className="text-sm flex items-center gap-1.5 transition-colors hover:text-[var(--gold-2)]" 
                            style={{ color: 'var(--muted)', fontWeight: 480 }}
                        >
                            <Ticket className="w-4 h-4" strokeWidth={1.75} />
                            Cupons
                        </Link>
                        {/* "Em Alta" e "Grátis" apontavam para lugares que não
                            correspondiam ao nome: não existe filtro de grátis. Ficou
                            um atalho só, com o nome do que ele de fato mostra. */}
                        <Link 
                            to="/?sort=discount" 
                            className="text-sm flex items-center gap-1.5 transition-colors hover:text-[var(--gold-2)]" 
                            style={{ color: 'var(--muted)', fontWeight: 480 }}
                        >
                            <TrendingUp className="w-4 h-4" strokeWidth={1.75} />
                            Maiores descontos
                        </Link>
                        {/* Dropdown Lojas (Sprint 1 #2) */}
                        <NavStoresDropdown />
                        {showPlans && (
                            <Link
                                to="/planos"
                                className="text-sm flex items-center gap-1.5 transition-colors hover:text-[var(--gold-2)]"
                                style={{ color: 'var(--gold-2)', fontWeight: 520 }}
                                data-testid="nav-planos"
                            >
                                <Crown className="w-4 h-4" strokeWidth={1.75} />
                                Planos
                            </Link>
                        )}
                        {/* Slot promo no menu (Sprint 1 #3) */}
                        <NavPromoSlots />
                    </div>
                </div>
            </div>
        </nav>
    );
};
